import { createFileRoute } from "@tanstack/react-router";
import {
  ShieldCheck,
  Check,
  CreditCard,
  QrCode,
  FileText,
  Clock,
  BookOpen,
  Calculator,
  Receipt,
  Briefcase,
  TrendingUp,
  Compass,
  Sparkles,
  Star,
} from "lucide-react";
import coverAsset from "@/assets/ebook-mei-2026-cover.png.asset.json";
import { trackEbookCheckoutReached } from "@/lib/analytics";

const CHECKOUT_URL = "https://pay.kiwify.com.br/zvzgEit";
const CANONICAL = "https://meitoolbox.com.br/guia-completo-mei-2026";

export const Route = createFileRoute("/guia-completo-mei-2026")({
  head: () => ({
    meta: [
      {
        title: "Manual MEI 2026: Guia Completo do MEI em PDF | MEI Toolbox",
      },
      {
        name: "description",
        content:
          "Manual MEI 2026 em PDF: DAS, nota fiscal, INSS, IRPF, limite de faturamento e direitos do MEI. Guia completo atualizado, entrega imediata por R$ 29,99.",
      },
      { name: "keywords", content: "manual MEI 2026, MEI 2026, guia MEI 2026, DAS MEI 2026, ebook MEI" },
      {
        property: "og:title",
        content: "Manual MEI 2026 — Guia Completo do MEI em PDF",
      },
      {
        property: "og:description",
        content:
          "Guia Completo do MEI 2026 em PDF: DAS, nota fiscal, INSS, IRPF e limite de faturamento. Atualizado para 2026, entrega imediata por R$ 29,99.",
      },
      { property: "og:site_name", content: "MEI Toolbox" },
      { property: "og:locale", content: "pt_BR" },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "product" },
      { property: "og:image", content: `https://meitoolbox.com.br${coverAsset.url}` },
      { property: "og:image:alt", content: "Capa do Manual MEI 2026 — Guia Completo do MEI em PDF" },
      { property: "product:price:amount", content: "29.99" },
      { property: "product:price:currency", content: "BRL" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Manual MEI 2026 — Guia Completo do MEI em PDF" },
      {
        name: "twitter:description",
        content:
          "DAS, nota fiscal, INSS, IRPF e limite de faturamento no Manual MEI 2026. PDF com entrega imediata por R$ 29,99.",
      },
      { name: "twitter:image", content: `https://meitoolbox.com.br${coverAsset.url}` },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: "MEI Sem Dúvidas 2026 — O Manual Definitivo Para Nunca Mais Errar com seu CNPJ",
          image: `https://meitoolbox.com.br${coverAsset.url}`,
          description:
            "E-book em PDF com 81 páginas ensinando tudo sobre MEI em 2026: DAS, nota fiscal, INSS, IRPF, golpes, funcionário e crescimento do negócio.",
          brand: { "@type": "Brand", name: "MEI Toolbox" },
          author: { "@type": "Person", name: "Ismaque de Oliveira" },
          offers: {
            "@type": "Offer",
            price: "29.99",
            priceCurrency: "BRL",
            availability: "https://schema.org/InStock",
            url: CANONICAL,
          },
        }),
      },
    ],
  }),
  component: LandingPage,
});

const painPoints = [
  '"Será que estou pagando o valor certo de DAS?"',
  '"Sou obrigado a emitir nota fiscal ou não?"',
  '"Se eu ultrapassar o limite, o que acontece com o meu CNPJ?"',
  '"Meu MEI me dá direito a aposentadoria mesmo?"',
  '"Como sei se um contato da Receita é golpe ou é real?"',
];

const modules = [
  {
    icon: BookOpen,
    title: "Fundamentos",
    text: "Como abrir, escolher atividade e formalizar sem errar.",
  },
  {
    icon: Calculator,
    title: "Impostos e Obrigações",
    text: "DAS, prazos, multas e como nunca mais atrasar.",
  },
  {
    icon: Receipt,
    title: "Notas Fiscais e Previdência",
    text: "Quando emitir nota e os benefícios do INSS que você já tem direito.",
  },
  {
    icon: Briefcase,
    title: "Operação do Negócio",
    text: "Funcionário, prestação de serviço e vendas online sem dor de cabeça.",
  },
  {
    icon: TrendingUp,
    title: "Finanças e Transições",
    text: "Imposto de Renda, crédito, golpes e o que fazer se o negócio crescer.",
  },
  {
    icon: Compass,
    title: "Futuro, Ferramentas e Referência",
    text: "Mudanças na lei, checklists prontos e respostas rápidas.",
  },
];

const bonuses = [
  { emoji: "📅", text: "Calendário anual do MEI, com todas as datas que importam" },
  { emoji: "📊", text: "Modelo de controle mensal de faturamento" },
  { emoji: "💰", text: "Modelo simples de fluxo de caixa" },
  { emoji: "📝", text: "Modelo de relatório mensal de receitas" },
  { emoji: "✅", text: "Checklist de regularidade, para saber se seu MEI está 100% em dia" },
  { emoji: "🛡️", text: "Guia rápido contra golpes" },
  { emoji: "📖", text: "Glossário completo dos termos do MEI" },
];

const faqs = [
  {
    q: "Recebo o e-book na hora?",
    a: "Sim, o acesso é liberado automaticamente assim que o pagamento é confirmado.",
  },
  {
    q: "Funciona no celular?",
    a: "Sim, o PDF foi formatado para leitura confortável tanto no celular quanto no computador.",
  },
  {
    q: "O conteúdo está mesmo atualizado para 2026?",
    a: "Sim, todas as informações foram revisadas com base nas regras vigentes em 2026, com destaque claro para o que ainda é projeto de lei.",
  },
  {
    q: "Isso substitui um contador?",
    a: "Não. O manual é educativo e ajuda você a entender suas obrigações, mas não substitui a orientação de um contador para o seu caso específico.",
  },
  {
    q: "E se eu tiver dúvida depois de comprar?",
    a: "O manual traz uma seção de perguntas frequentes e links oficiais para consulta — e o MEI Toolbox segue com conteúdo gratuito no site para te ajudar além do livro.",
  },
  {
    q: "O preço pode subir?",
    a: "Este é o valor de lançamento — o manual pode ser reajustado nas próximas atualizações.",
  },
  {
    q: "Como funciona a garantia de 7 dias?",
    a: "Se você comprar e, por qualquer motivo, achar que o manual não é para você, pode pedir reembolso total em até 7 dias após a compra, direto pela Kiwify — sem burocracia.",
  },
];

// Brand tokens (per user brief)
const NAVY = "#0B2A5B";
const GREEN = "#2FAE60";

function CTAButton({
  children,
  className = "",
  location = "lp_cta",
  ctaText,
}: {
  children: React.ReactNode;
  className?: string;
  location?: string;
  ctaText?: string;
}) {
  return (
    <a
      href={CHECKOUT_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => {
        const label =
          ctaText ?? (typeof children === "string" ? children : "Quero meu manual");
        trackEbookCheckoutReached(location, label, CHECKOUT_URL);
      }}
      className={
        "inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 text-base sm:text-lg font-extrabold text-white shadow-lg transition-transform hover:scale-[1.02] active:scale-[0.99] " +
        className
      }
      style={{ backgroundColor: GREEN, boxShadow: "0 10px 30px -8px rgba(47,174,96,0.55)" }}
    >
      {children}
    </a>
  );
}

function GuaranteeSeal({ className = "" }: { className?: string }) {
  return (
    <div
      className={
        "relative inline-flex flex-col items-center justify-center rounded-full text-center " +
        className
      }
      style={{
        width: 128,
        height: 128,
        background:
          "radial-gradient(circle at 30% 30%, #FFE58A 0%, #F5C542 45%, #B8860B 100%)",
        boxShadow: "0 8px 24px -6px rgba(184,134,11,0.55), inset 0 0 0 3px rgba(255,255,255,0.35)",
        color: "#3b2a05",
      }}
      aria-label="Selo de Garantia de 7 dias"
    >
      <ShieldCheck className="h-6 w-6 mb-0.5" />
      <div className="text-[10px] font-bold uppercase tracking-wider leading-tight">
        Garantia
      </div>
      <div className="text-lg font-black leading-none">7 dias</div>
      <div className="text-[8.5px] font-semibold px-2 leading-tight mt-1">
        Satisfação ou dinheiro de volta
      </div>
    </div>
  );
}

function PaymentBadges() {
  return (
    <div className="flex flex-wrap items-center justify-center gap-2 text-xs sm:text-sm">
      <span className="inline-flex items-center gap-1.5 rounded-full bg-white/95 border border-slate-200 px-3 py-1.5 font-semibold text-slate-700 shadow-sm">
        <CreditCard className="h-3.5 w-3.5" /> Cartão
      </span>
      <span className="inline-flex items-center gap-1.5 rounded-full bg-white/95 border border-slate-200 px-3 py-1.5 font-semibold text-slate-700 shadow-sm">
        <QrCode className="h-3.5 w-3.5" /> Pix
      </span>
      <span className="inline-flex items-center gap-1.5 rounded-full bg-white/95 border border-slate-200 px-3 py-1.5 font-semibold text-slate-700 shadow-sm">
        <FileText className="h-3.5 w-3.5" /> Boleto
      </span>
    </div>
  );
}

function LandingPage() {
  return (
    <div className="bg-slate-50 text-slate-900 antialiased" style={{ fontFamily: "Inter, Arial, system-ui, sans-serif" }}>
      {/* HERO */}
      <section
        className="relative overflow-hidden text-white"
        style={{
          background: `linear-gradient(135deg, ${NAVY} 0%, #143a7a 60%, #0B2A5B 100%)`,
        }}
      >
        <div
          aria-hidden
          className="absolute inset-0 opacity-30"
          style={{
            background:
              "radial-gradient(circle at 80% 10%, rgba(47,174,96,0.35), transparent 50%), radial-gradient(circle at 10% 80%, rgba(255,255,255,0.08), transparent 55%)",
          }}
        />
        <div className="relative container mx-auto px-4 py-10 sm:py-16 md:py-20">
          <div className="grid md:grid-cols-2 gap-10 md:gap-12 items-center">
            <div className="order-2 md:order-1">
              <div
                className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[11px] sm:text-xs font-bold uppercase tracking-wider"
                style={{ backgroundColor: GREEN }}
              >
                <Sparkles className="h-3.5 w-3.5" /> Atualizado para 2026
              </div>
              <h1 className="mt-5 text-3xl sm:text-4xl md:text-5xl font-black leading-[1.1] text-balance">
                Manual MEI 2026: seu CNPJ não vem com instruções.{" "}
                <span style={{ color: GREEN }}>Este vem.</span>
              </h1>
              <p className="mt-5 text-base sm:text-lg text-white/85 max-w-xl">
                O Guia Completo do MEI 2026 tira toda dúvida sobre DAS, nota
                fiscal, INSS, IRPF e limite de faturamento — em linguagem
                simples e com fontes oficiais.
              </p>

              <div className="mt-7 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <CTAButton>Quero meu manual agora — R$ 29,99</CTAButton>
                <GuaranteeSeal className="hidden sm:inline-flex" />
              </div>

              <div className="mt-5">
                <PaymentBadges />
              </div>

              <div className="mt-5 flex items-center gap-3 text-sm text-white/80">
                <div className="flex -space-x-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <span>81 páginas · PDF · Entrega imediata</span>
              </div>
            </div>

            <div className="order-1 md:order-2 flex justify-center">
              <div className="relative">
                <div
                  aria-hidden
                  className="absolute -inset-8 rounded-full blur-3xl opacity-40"
                  style={{ background: `radial-gradient(circle, ${GREEN} 0%, transparent 70%)` }}
                />
                <img
                  src={coverAsset.url}
                  alt="Capa do e-book MEI Sem Dúvidas 2026"
                  width={480}
                  height={640}
                  className="relative w-[240px] sm:w-[300px] md:w-[360px] h-auto drop-shadow-2xl rotate-[-4deg] hover:rotate-0 transition-transform duration-500"
                />
                <div className="sm:hidden mt-6 flex justify-center">
                  <GuaranteeSeal />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PAIN */}
      <section className="container mx-auto px-4 py-14 sm:py-20 max-w-4xl">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-center" style={{ color: NAVY }}>
          Se você já se pegou pensando nisso, este manual é para você
        </h2>
        <ul className="mt-8 grid gap-3 sm:gap-4">
          {painPoints.map((p, i) => (
            <li
              key={i}
              className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm"
            >
              <span
                className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white text-sm font-bold"
                style={{ backgroundColor: NAVY }}
              >
                ?
              </span>
              <span className="text-base sm:text-lg text-slate-800 italic">{p}</span>
            </li>
          ))}
        </ul>
        <p className="mt-8 text-center text-slate-600 text-base sm:text-lg max-w-2xl mx-auto">
          Se alguma dessas perguntas já passou pela sua cabeça, você não está
          sozinho — e a resposta certa está a poucas páginas de distância.
        </p>
      </section>

      {/* SOLUTION */}
      <section className="bg-white border-y border-slate-200">
        <div className="container mx-auto px-4 py-14 sm:py-20 max-w-4xl text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black" style={{ color: NAVY }}>
            Chega de informação contraditória
          </h2>
          <p className="mt-6 text-base sm:text-lg text-slate-700 leading-relaxed">
            Existem centenas de vídeos e posts sobre MEI na internet — o problema
            é que muitos estão desatualizados, incompletos ou simplesmente
            errados. O <strong>MEI Sem Dúvidas 2026</strong> foi escrito e
            pesquisado direto nas fontes oficiais (Receita Federal, INSS, Portal
            do Empreendedor, Sebrae), com linguagem simples e sem juridiquês.
            Você não precisa mais adivinhar — precisa só abrir o manual certo.
          </p>
        </div>
      </section>

      {/* MODULES */}
      <section className="container mx-auto px-4 py-14 sm:py-20">
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black" style={{ color: NAVY }}>
            O que você aprende no Manual MEI 2026
          </h2>
          <p className="mt-3 text-slate-600">
            Os 6 blocos temáticos do Guia Completo do MEI 2026, direto ao ponto.
          </p>
        </div>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {modules.map((m) => (
            <div
              key={m.title}
              className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-md transition-shadow"
            >
              <div
                className="flex h-12 w-12 items-center justify-center rounded-xl text-white"
                style={{ backgroundColor: NAVY }}
              >
                <m.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-4 text-lg font-bold" style={{ color: NAVY }}>
                {m.title}
              </h3>
              <p className="mt-2 text-slate-600 text-[15px] leading-relaxed">{m.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* AUTHORITY */}
      <section className="bg-slate-100 border-y border-slate-200">
        <div className="container mx-auto px-4 py-14 sm:py-20 max-w-4xl">
          <div className="text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black" style={{ color: NAVY }}>
              Informação de fonte oficial, sem "achismo"
            </h2>
            <p className="mt-5 text-base sm:text-lg text-slate-700 leading-relaxed max-w-3xl mx-auto">
              Cada capítulo foi construído com base em fontes como Receita
              Federal, INSS, Portal do Empreendedor e Sebrae — e sempre que uma
              regra ainda é só um projeto de lei, o manual deixa isso bem claro,
              para você nunca tomar decisão em cima de informação que ainda nem
              virou lei.
            </p>
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-2 sm:gap-3">
            {["Receita Federal", "INSS", "Portal do Empreendedor", "Sebrae", "gov.br"].map(
              (s) => (
                <span
                  key={s}
                  className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm"
                >
                  <ShieldCheck className="h-4 w-4" style={{ color: GREEN }} /> {s}
                </span>
              ),
            )}
          </div>
        </div>
      </section>

      {/* BONUSES */}
      <section className="container mx-auto px-4 py-14 sm:py-20 max-w-4xl">
        <div className="text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black" style={{ color: NAVY }}>
            Além do manual, você leva de bônus
          </h2>
          <p className="mt-3 text-slate-600">
            Tudo pronto para usar já no primeiro dia.
          </p>
        </div>
        <ul className="mt-10 grid gap-3 sm:grid-cols-2">
          {bonuses.map((b, i) => (
            <li
              key={i}
              className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm"
            >
              <span className="text-2xl leading-none">{b.emoji}</span>
              <span className="text-[15px] sm:text-base text-slate-800 leading-snug">
                {b.text}
              </span>
            </li>
          ))}
        </ul>
      </section>

      {/* OFFER */}
      <section
        className="text-white"
        style={{
          background: `linear-gradient(135deg, ${NAVY} 0%, #143a7a 60%, #0B2A5B 100%)`,
        }}
      >
        <div className="container mx-auto px-4 py-14 sm:py-20">
          <div className="grid md:grid-cols-2 gap-10 md:gap-14 items-center max-w-5xl mx-auto">
            <div className="flex justify-center">
              <img
                src={coverAsset.url}
                alt="MEI Sem Dúvidas 2026 — capa"
                width={400}
                height={533}
                loading="lazy"
                className="w-[220px] sm:w-[280px] md:w-[320px] h-auto drop-shadow-2xl"
              />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-black leading-tight">
                Menos que um lanche, mais tranquilidade o ano inteiro
              </h2>
              <p className="mt-5 text-white/85 text-base sm:text-lg leading-relaxed">
                Por <strong className="text-white">R$ 29,99</strong>, você recebe
                o manual completo em PDF, com entrega imediata após a compra,
                para ler no celular, tablet ou computador — e consultar sempre
                que uma dúvida aparecer. Pagamento processado com segurança pela
                Kiwify, em cartão de crédito, Pix ou boleto.
              </p>

              <div className="mt-8 rounded-3xl bg-white/10 border border-white/20 backdrop-blur p-6 sm:p-8">
                <div className="flex items-baseline gap-2">
                  <span className="text-white/70 text-sm line-through">R$ 49,90</span>
                  <span className="text-4xl sm:text-5xl font-black" style={{ color: GREEN }}>
                    R$ 29,99
                  </span>
                </div>
                <p className="mt-1 text-white/75 text-sm">
                  <Clock className="inline h-4 w-4 mr-1" /> Preço de lançamento ·
                  Entrega imediata
                </p>

                <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <CTAButton className="w-full sm:w-auto">
                    Quero meu manual agora — R$ 29,99
                  </CTAButton>
                  <GuaranteeSeal />
                </div>

                <div className="mt-5">
                  <PaymentBadges />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="container mx-auto px-4 py-14 sm:py-20 max-w-3xl">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-center" style={{ color: NAVY }}>
          Perguntas frequentes
        </h2>
        <div className="mt-10 space-y-3">
          {faqs.map((f, i) => (
            <details
              key={i}
              className="group rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm open:shadow-md transition"
            >
              <summary className="cursor-pointer list-none flex items-start justify-between gap-3 font-bold text-slate-900">
                <span className="text-[15px] sm:text-base">{f.q}</span>
                <span
                  className="shrink-0 flex h-8 w-8 items-center justify-center rounded-full text-white group-open:rotate-45 transition-transform"
                  style={{ backgroundColor: NAVY }}
                >
                  +
                </span>
              </summary>
              <p className="mt-3 text-slate-600 leading-relaxed text-[15px] sm:text-base">
                {f.a}
              </p>
            </details>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section
        className="text-white"
        style={{
          background: `linear-gradient(135deg, ${NAVY} 0%, #143a7a 100%)`,
        }}
      >
        <div className="container mx-auto px-4 py-14 sm:py-20 max-w-3xl text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black leading-tight">
            Você não precisa continuar na dúvida
          </h2>
          <p className="mt-5 text-white/85 text-base sm:text-lg">
            Em poucos minutos você pode ter todas as respostas sobre o seu MEI
            organizadas em um só lugar — sem depender de vídeo desatualizado ou
            grupo de WhatsApp.
          </p>
          <div className="mt-8 flex flex-col items-center gap-5">
            <CTAButton>Quero meu manual agora — R$ 29,99</CTAButton>
            <PaymentBadges />
            <div className="flex items-center gap-2 text-white/80 text-sm">
              <Check className="h-4 w-4" style={{ color: GREEN }} /> Garantia de
              7 dias · Reembolso pela Kiwify sem burocracia
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-slate-900 text-slate-400 text-sm">
        <div className="container mx-auto px-4 py-8 max-w-4xl text-center space-y-2">
          <p>
            © 2026 <strong className="text-white">MEI Toolbox®</strong>. Todos os
            direitos reservados.
          </p>
          <p className="text-xs text-slate-500 max-w-2xl mx-auto">
            Este material tem finalidade educativa e não substitui a orientação
            de um contador, advogado ou órgão oficial. Autor: Ismaque de
            Oliveira.
          </p>
        </div>
      </footer>

      {/* STICKY MOBILE CTA */}
      <div className="fixed bottom-0 inset-x-0 z-40 md:hidden p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] bg-white/95 backdrop-blur border-t border-slate-200 shadow-[0_-8px_24px_-8px_rgba(0,0,0,0.15)]">
        <a
          href={CHECKOUT_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() =>
            trackEbookCheckoutReached(
              "lp_sticky_mobile",
              "Quero meu manual — R$ 29,99",
              CHECKOUT_URL,
            )
          }
          className="flex items-center justify-center gap-2 w-full rounded-full py-3.5 text-base font-extrabold text-white"
          style={{ backgroundColor: GREEN }}
        >
          Quero meu manual — R$ 29,99
        </a>
      </div>
      {/* spacer so sticky cta doesn't cover footer on mobile */}
      <div className="h-20 md:hidden" aria-hidden />
    </div>
  );
}
